import cx from 'classnames';
import {useContext, useMemo, useCallback} from 'react';

import {Loader, Icon, RequiredMark} from 'components';
import {UserSessionContext} from 'context';
import {useUserSession} from 'hooks';
import {SessionScreenshot} from 'interfaces';

import {ScreenshotPreview} from '../ScreenshotPreview';
import {InSessionAction} from '../types';

type Props = {
    screenshot: SessionScreenshot;
    disabled?: boolean;
    participantId?: string;
    onClick: (screenshot: SessionScreenshot, participantId?: string) => void;
}

export const ScreenshotPlaceholder: React.FC<Props> = ({screenshot, disabled, participantId, onClick}: Props) => {
    const {screenshotsPreview, inProgressActions, onRequestDeleteScreenShot} = useContext(UserSessionContext);
    const {isScreenshotsPreviewEnabled} = useUserSession();

    const action = useMemo(() =>
        inProgressActions.find((a) => a.action === InSessionAction.ToggleScreenshot
            && a.data?.screenshotName === screenshot.name
            && (!participantId || a.data?.participantId === participantId)),
    [screenshot, inProgressActions, participantId]);

    const loading = useMemo(() => action && !action.error, [action]);

    const receivedForOther = useMemo(() =>
        screenshot.received && participantId && screenshot.participantId
        && screenshot.participantId !== participantId,
    [screenshot, participantId]);

    const inProgressForOther = useMemo(() =>
        participantId && inProgressActions.some((a) => a.action === InSessionAction.ToggleScreenshot
            && a.data?.screenshotName === screenshot.name
            && a.data?.participantId && a.data?.participantId !== participantId),
    [screenshot, inProgressActions, participantId]);

    const activeState = useMemo(() => {
        if (loading) {
            return 'loading';
        } else if (receivedForOther || inProgressForOther) {
            return 'disabled';
        } else if (!loading && screenshot.received && !screenshotsPreview) {
            return isScreenshotsPreviewEnabled ? 'received' : 'delete';
        } else if (!loading && !screenshot.received) {
            return disabled ? 'disabled' : 'create';
        }
    }, [screenshot, loading, disabled, screenshotsPreview, isScreenshotsPreviewEnabled, receivedForOther, inProgressForOther]);

    const handleClick = useCallback(() => {
        if (activeState === 'delete') {
            onRequestDeleteScreenShot(screenshot);
        } else if (activeState !== 'disabled') {
            onClick(screenshot, participantId);
        }
    }, [activeState, screenshot, participantId, onClick, onRequestDeleteScreenShot]);

    const clickable = useMemo(() => activeState !== 'loading' && activeState !== 'disabled', [activeState]);

    return (
        <div className='tw-w-30'>
            <div
                className={cx(
                    'tw-w-30 tw-h-30 tw-border tw-border-dashed tw-border-default tw-flex-center tw-relative',
                    {'tw-cursor-pointer': clickable, 'tw-cursor-not-allowed': activeState === 'disabled'}
                )}
                onClick={handleClick}
            >
                {screenshot.received && !receivedForOther &&
                    <ScreenshotPreview
                        screenshot={screenshot}
                        hidden={!screenshotsPreview || loading}
                    />}
                {activeState === 'loading' && <Loader svgClassName='tw-max-w-8 tw-max-h-8' />}
                {activeState === 'received' && <Icon name='check-circle' size='3xl' color='success' thickness='sm' />}
                {activeState === 'delete' && <Icon name='delete' size='3xl' color='alert' thickness='sm' />}
                {activeState === 'disabled' && <Icon name='slash' size='3xl' color='default-dimmed' thickness='sm' />}
                {activeState === 'create' && <Icon name='plus' size='3xl' color='default-dimmed' thickness='sm' />}
            </div>
            <span className='tw-break-words'>
                {screenshot.name}
                {screenshot.required && <RequiredMark />}
            </span>
        </div>);
};